// QrLandingView — cieľ QR/NFC skenu (P5b, Mirko 2026-06-10)
// QR kóduje origin/v2/qr/TYP-NN → tu sa identifikuje stroj (alebo NFC tag) a zobrazí karta.
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchAuth } from '../auth/fetchAuth.js'
import { COLORS, TYPO, SPACE, RADIUS, hladaj } from '../theme.js'

const STATUS = {
  dostupne:  { l: 'Dostupné',  c: '#15803d' },
  pozicane:  { l: 'Požičané',  c: '#b45309' },
  v_servise: { l: 'V servise', c: '#dc2626' },
  vyradene:  { l: 'Vyradené',  c: '#6b7280' },
}
const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toUpperCase()
const codeNum = (c) => { const m = String(c || '').match(/\d+/); return m ? parseInt(m[0], 10) : null }
const fmtCode = (c) => { const x = String(c || '').trim(); if (!x) return ''; return /^\d+$/.test(x) ? 'č.' + x : x }
const btn = (c) => ({ padding: '11px 16px', background: c, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' })
const btnGhost = () => ({ padding: '11px 16px', background: '#fff', color: COLORS.nav, border: `1px solid ${COLORS.line}`, borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' })

export default function QrLandingView({ code }) {
  const navigate = useNavigate()
  const [item, setItem] = useState(undefined) // undefined=loading, null=not found
  const [loan, setLoan] = useState(null)
  const [part, setPart] = useState(null) // ak QR patrí konkrétnemu dielu (rám/motor/kompresor)
  const [poolCode, setPoolCode] = useState(null) // predtlačený QR kód (58.11), ktorý ešte nie je priradený položke
  const [allItems, setAllItems] = useState([])
  const [refresh, setRefresh] = useState(0)      // po priradení kódu sa matching spustí znova
  useEffect(() => {
    let on = true
    ;(async () => {
      try {
        const items = await fetchAuth('/api/loan-items').then(r => r.ok ? r.json() : []).catch(() => [])
        const arr = Array.isArray(items) ? items : []
        const raw = String(code || '').trim()
        let found = null
        const partLabel = null   // per-diel SN- štítky boli zrušené spolu s ich generátorom
        // predtlačený 6-znakový kód (58.11) — priradený na položke ako uid
        const normed = raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
        if (normed) found = arr.find(x => (x.uid || '').trim() && x.uid.trim().toUpperCase() === normed)
        // trvalé id kusu
        if (!found) found = arr.find(x => String(x.id) === raw)
        // NFC štítok, ak ho niekto na kus nalepil
        if (!found) found = arr.find(x => (x.nfc_tag || '').trim() && x.nfc_tag.trim().toUpperCase() === raw.toUpperCase())
        // stále nič → je to voľný predtlačený kód z poolu? (nálepka nalepená, ešte nepriradená)
        let pool = null
        if (!found && normed) {
          pool = await fetchAuth('/api/loan-qr/' + encodeURIComponent(normed)).then(r => r.ok ? r.json() : null).catch(() => null)
          if (pool && (pool.item_id || '').trim()) {
            found = arr.find(x => String(x.id) === pool.item_id) || null
            if (found) pool = null
          }
        }
        if (!on) return
        setAllItems(arr)
        setPoolCode(pool)
        setPart(partLabel)
        setItem(found || null)
        if (found && found.status === 'pozicane') {
          const lr = await fetchAuth(`/api/loan-records?item_id=${found.id}&active_only=1`).then(r => r.ok ? r.json() : []).catch(() => [])
          if (on) setLoan((Array.isArray(lr) ? lr : [])[0] || null)
        }
      } catch (_) { if (on) setItem(null) }
    })()
    return () => { on = false }
  }, [code, refresh])

  const wrap = { maxWidth: 520, margin: '0 auto', padding: SPACE.lg }
  const st = item ? (STATUS[item.status] || { l: item.status, c: COLORS.muted }) : null
  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg }}>
      <div style={{ background: COLORS.brand, color: '#fff', padding: '14px 18px', fontWeight: 800, fontSize: 16 }}>QR / NFC sken</div>
      <div style={wrap}>
        <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 8 }}>Naskenovaný kód: <b style={{ fontFamily: 'monospace' }}>{code}</b></div>
        {item === undefined ? (
          <div style={{ color: COLORS.muted }}>Načítavam…</div>
        ) : item ? (
          <div style={{ background: '#fff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, padding: SPACE.lg }}>
            <div style={{ fontSize: 20, fontWeight: 900 }}>{item.name} {(fmtCode(item.code) && !(item.name || '').includes(fmtCode(item.code))) ? <span style={{ color: COLORS.brand }}>{fmtCode(item.code)}</span> : null}</div>
            <div style={{ marginTop: 8, display: 'inline-block', padding: '4px 13px', borderRadius: 999, background: st.c, color: '#fff', fontWeight: 800, fontSize: 13 }}>{st.l}</div>
            {part ? <div style={{ marginTop: 8, marginLeft: 8, display: 'inline-block', padding: '4px 13px', borderRadius: 999, background: COLORS.nav, color: '#fff', fontWeight: 800, fontSize: 13 }}>Diel: {part}</div> : null}
            <div style={{ marginTop: 12, fontSize: 14, color: COLORS.text, lineHeight: 1.7 }}>
              <div><b>Kategória:</b> {item.category || '—'}</div>
              {item.description ? <div><b>Popis:</b> {item.description}</div> : null}
              {item.nfc_tag ? <div><b>NFC tag:</b> {item.nfc_tag}</div> : null}
              {loan ? <><div><b>Požičané:</b> {loan.employee_name || '—'}</div><div><b>Zákazka:</b> {loan.project_id || '—'}{loan.project_name ? ' · ' + loan.project_name : ''}</div></> : null}
              {item.frame_serial ? <div><b>Rám:</b> č. {item.frame_serial}</div> : null}
              {item.motor_serial ? <div><b>Motor:</b> č. {item.motor_serial}</div> : null}
              {item.compressor_serial ? <div><b>Kompresor:</b> č. {item.compressor_serial}{item.compressor_color ? ' · ' + item.compressor_color : ''}</div> : null}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/katalog/pozicovna')} style={btn(COLORS.brand)}>Detail stroja</button>
              <button onClick={() => navigate('/pozicovna')} style={btnGhost()}>Požičovňa</button>
            </div>
          </div>
        ) : poolCode ? (
          <AssignPoolCard pool={poolCode} items={allItems} onAssigned={() => setRefresh(n => n + 1)} />
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, padding: SPACE.lg }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Stroj sa nenašiel</div>
            <div style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.6 }}>Pre kód <b>{code}</b> nie je v katalógu žiadny stroj. Možno je to diel evidovaný v rámci omietačky, alebo stroj ešte nemá priradený tento kód.</div>
            <button onClick={() => navigate('/katalog/pozicovna')} style={{ ...btn(COLORS.brand), marginTop: 14 }}>Otvoriť katalóg</button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Priradenie voľného predtlačeného QR kódu položke priamo zo skenu (58.11) ──
// Flow: nálepka sa nalepí na náradie → sken telefónom → tu sa vyberie položka → hotovo.
function AssignPoolCard({ pool, items, onAssigned }) {
  const [q, setQ] = useState('')
  const [pick, setPick] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const candidates = (items || [])
    .filter(x => x.active !== 0 && !(x.uid || '').trim() && !(x.parent_id || '').trim())
    .filter(x => { const s = q.trim().toLowerCase(); if (!s) return true; return hladaj(((x.name || '') + ' ' + (x.code || '') + ' ' + (x.serial_number || '') + ' ' + (x.category || '')), s) })
    .slice(0, 30)
  const assign = async () => {
    if (!pick) return
    setBusy(true); setErr('')
    try {
      const r = await fetchAuth(`/api/loan-qr/${encodeURIComponent(pool.code)}/assign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item_id: pick.id }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error === 'FORBIDDEN' ? 'Nemáš oprávnenie priraďovať QR kódy.' : d.error === 'ALREADY_ASSIGNED' ? 'Kód medzičasom priradil niekto iný.' : d.error === 'ITEM_HAS_CODE' ? ('Položka už má kód ' + (d.uid || '') + '.') : (d.error || 'Priradenie zlyhalo.'))
      onAssigned()
    } catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <div style={{ background: '#fff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, padding: SPACE.lg }}>
      <div style={{ display: 'inline-block', padding: '4px 13px', borderRadius: 999, background: COLORS.brand, color: '#fff', fontWeight: 800, fontSize: 13 }}>Nepriradený QR kód</div>
      <div style={{ fontSize: 20, fontWeight: 900, marginTop: 10, fontFamily: 'monospace', letterSpacing: 2 }}>{pool.code}</div>
      <div style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.6, marginTop: 6 }}>Táto nálepka ešte nie je priradená žiadnemu náradiu. Vyber položku z katalógu, na ktorú si ju nalepil — odteraz bude sken otvárať jej kartu.</div>
      <input value={q} onChange={e => { setQ(e.target.value); setPick(null) }} placeholder="Hľadať položku (názov, kód, výr. č.)…"
        style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, padding: '11px 12px', fontSize: 15, border: `1px solid ${COLORS.line}`, borderRadius: 10, fontFamily: 'inherit' }} />
      <div style={{ marginTop: 8, maxHeight: 300, overflow: 'auto', border: `1px solid ${COLORS.line}`, borderRadius: 10 }}>
        {candidates.length === 0 ? (
          <div style={{ padding: 14, fontSize: 13, color: COLORS.muted }}>Žiadna voľná položka (bez QR kódu) nezodpovedá hľadaniu.</div>
        ) : candidates.map(x => (
          <button key={x.id} onClick={() => setPick(x)}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 12px', minHeight: 44, cursor: 'pointer', fontFamily: 'inherit', background: pick && pick.id === x.id ? COLORS.brand : '#fff', color: pick && pick.id === x.id ? '#fff' : COLORS.text, border: 'none', borderBottom: `1px solid ${COLORS.line}` }}>
            <span style={{ fontWeight: 700 }}>{x.name}</span>{fmtCode(x.code) ? <span style={{ fontWeight: 800, marginLeft: 6, color: pick && pick.id === x.id ? '#fff' : COLORS.brand }}>{fmtCode(x.code)}</span> : null}
            <span style={{ fontSize: 12, marginLeft: 6, opacity: .75 }}>{x.category || ''}{x.serial_number ? ' · SN ' + x.serial_number : ''}</span>
          </button>
        ))}
      </div>
      {err ? <div style={{ marginTop: 10, fontSize: 13, color: '#dc2626', fontWeight: 700 }}>{err}</div> : null}
      <button onClick={assign} disabled={!pick || busy}
        style={{ ...btn(pick && !busy ? '#15803d' : '#9ca3af'), width: '100%', marginTop: 12, padding: '14px 16px', fontSize: 15, cursor: pick && !busy ? 'pointer' : 'default' }}>
        {busy ? 'Priraďujem…' : pick ? `Priradiť kód ${pool.code} → ${pick.name}` : 'Najprv vyber položku'}
      </button>
    </div>
  )
}
